import type { DataModelAttribute, Item } from '@/types/DataModel';

import type {
    ChannelDestination,
    CommunicationPlan,
    CommunicationPlans,
    ItemsByChannel,
    ItemSelection,
    OrganizationDestination,
    PlanOptions,
    PublicationData,
    PublicationDestination,
    PublicationJob,
    PublicationMode,
    PublicationValuesPayload,
} from '../types';
import { PUBLICATION_MODE } from '../types';
import { isEmptyValue, isNil } from './publicationDestinations';

const getChannelId = (channelOrDestination: {
    id?: number | string;
    destinationId?: number | string;
}): string => {
    return String(channelOrDestination.id ?? channelOrDestination.destinationId);
};

const findCommunicationChannel = (
    destination: PublicationDestination,
    communicationChannels: ChannelDestination[],
): ChannelDestination | undefined => {
    return communicationChannels.find((channel) => {
        return getChannelId(channel) === getChannelId(destination) &&
            (channel.subDestinationKey || null) === (destination.subDestination || null);
    });
};

const getPublicationAttributes = (
    planName: string | undefined,
    plans: CommunicationPlans,
    mode: PublicationMode,
): DataModelAttribute[] => {
    if (!planName) {
        return [];
    }
    const plan = plans[planName];
    if (!plan) {
        return [];
    }
    const attributes = mode === PUBLICATION_MODE.PUBLISH
        ? plan.publicationAttributes
        : plan.unpublicationAttributes;
    return attributes ?? [];
};

const getPublicationItemCategoryOfChannel = (
    channel: ChannelDestination | undefined,
    plans: CommunicationPlans,
    mode: PublicationMode,
): string | null => {
    if (!channel?.plan) {
        return null;
    }
    const plan: CommunicationPlan | undefined = plans[channel.plan];
    const categoryType = mode === PUBLICATION_MODE.PUBLISH
        ? 'publicationCategory'
        : 'unpublicationCategory';
    return plan ? (plan[categoryType] ?? null) : null;
};

const pickPublicationValuesForPlan = (
    planName: string | undefined,
    channelItem: Item | undefined,
    channel: ChannelDestination | undefined,
    plans: CommunicationPlans,
    mode: PublicationMode,
): Record<string, unknown> => {
    if (isEmptyValue(channelItem)) {
        return {};
    }
    const values: Record<string, unknown> = {};
    if (!isEmptyValue(channelItem?.category__)) {
        values.category__ = structuredClone(channelItem?.category__);
    } else if (!isEmptyValue(channel)) {
        const publicationCategoryName = getPublicationItemCategoryOfChannel(channel, plans, mode);
        if (!isEmptyValue(publicationCategoryName)) {
            values.category__ = publicationCategoryName;
        }
    }
    getPublicationAttributes(planName, plans, mode).forEach((attribute) => {
        const value = channelItem?.[attribute.name];
        if (!isNil(value) && value !== '') {
            values[attribute.name] = structuredClone(value);
        }
    });
    return values;
};

const buildPublicationValuesPerDestinationChannel = (
    channelDestinations: PublicationDestination[],
    communicationChannels: ChannelDestination[],
    itemsByChannel: ItemsByChannel,
    plans: CommunicationPlans,
    mode: PublicationMode,
): Record<string, Record<string, unknown>> => {
    const result: Record<string, Record<string, unknown>> = {};
    channelDestinations.forEach((destination) => {
        const channel = findCommunicationChannel(destination, communicationChannels);
        const channelItem = channel ? itemsByChannel[getChannelId(channel)] : undefined;
        const values = pickPublicationValuesForPlan(
            channel?.plan,
            channelItem,
            channel,
            plans,
            mode,
        );
        if (!isEmptyValue(values) && channel) {
            result[getChannelId(channel)] = values;
        }
    });
    return result;
};

const buildPublicationValuesPayload = (
    channelDestinations: PublicationDestination[],
    communicationChannels: ChannelDestination[],
    itemsByChannel: ItemsByChannel,
    plans: CommunicationPlans,
    mode: PublicationMode,
): PublicationValuesPayload => {
    const publicationValuesPerDestinationChannel = buildPublicationValuesPerDestinationChannel(
        channelDestinations,
        communicationChannels,
        itemsByChannel,
        plans,
        mode,
    );
    if (!isEmptyValue(publicationValuesPerDestinationChannel)) {
        return {
            publicationValues: null,
            publicationValuesPerDestinationChannel,
        };
    }
    return {
        publicationValues: undefined,
        publicationValuesPerDestinationChannel: undefined,
    };
};

const getSavedValuesForChannel = (
    channelId: string,
    job?: PublicationJob | null,
): Record<string, unknown> | null => {
    if (isEmptyValue(job)) {
        return null;
    }
    const perDestinationValues = job?.publicationValuesPerDestinationChannel;
    if (!isEmptyValue(perDestinationValues)) {
        if (!isEmptyValue(perDestinationValues?.[channelId])) {
            return perDestinationValues?.[channelId] ?? null;
        }
        const subDestinationKey = Object.keys(perDestinationValues ?? {}).find((key) => {
            return key.indexOf(`${channelId}:`) === 0;
        });
        if (subDestinationKey && perDestinationValues) {
            return perDestinationValues[subDestinationKey];
        }
    }
    return job?.publicationValues ?? null;
};

const getSavedPublicationValues = (
    job?: PublicationJob | null,
): Record<string, unknown> | null => {
    if (isEmptyValue(job)) {
        return null;
    }
    if (!isEmptyValue(job?.publicationValues)) {
        return job?.publicationValues ?? null;
    }
    const perDestinationValues = job?.publicationValuesPerDestinationChannel;
    if (isEmptyValue(perDestinationValues)) {
        return null;
    }
    return Object.values(perDestinationValues ?? {})[0] ?? null;
};

const applyAttributeDefaults = (
    attributes: DataModelAttribute[],
    item: Item,
): Item => {
    const next = { ...item };
    attributes.forEach((attribute) => {
        const defaultValue = (attribute as DataModelAttribute & { defaultValue?: unknown }).defaultValue
            ?? attribute.params?.defaultValue;
        if ((isNil(next[attribute.name]) || next[attribute.name] === '') &&
            !isNil(defaultValue)) {
            next[attribute.name] = structuredClone(defaultValue);
        }
    });
    return next;
};

const mergeChannelDefaultItem = (
    existing: Item | undefined,
    categoryName: string | null,
    defaultItem: Item,
    attributes: DataModelAttribute[],
): Item => {
    const merged: Item = existing
        ? { ...existing, ...structuredClone(defaultItem) }
        : { ...structuredClone(defaultItem) };
    if (isEmptyValue(merged.category__) && !isEmptyValue(categoryName) && categoryName) {
        merged.category__ = categoryName;
    }
    return applyAttributeDefaults(attributes, merged);
};

const applySavedValuesToItem = (
    item: Item,
    savedValues: Record<string, unknown> | null,
    selectedPublicationMode: unknown,
    categoryAttributes: DataModelAttribute[],
): Item => {
    const next = { ...item };
    if (!isEmptyValue(savedValues) && savedValues) {
        const jobValues = { ...savedValues };
        delete jobValues.publication_mode;
        Object.assign(next, jobValues);
    }
    if (!isNil(selectedPublicationMode) &&
        categoryAttributes.some((attribute) => attribute.name === 'publication_mode')) {
        next.publication_mode = selectedPublicationMode;
    }
    return next;
};

const mapOrganizationPublication = (organization: OrganizationDestination): PublicationDestination => ({
    destinationId: organization.organizationId,
    destinationType: 'IN_PLATFORM',
});

const mapChannelPublication = (channel: ChannelDestination): PublicationDestination => ({
    destinationId: channel.id,
    destinationType: 'COMMUNICATION_CHANNEL',
    subDestination: channel.subDestinationKey,
});

const getSelectedPublicationDestinations = (
    organizations: OrganizationDestination[],
    communicationChannels: ChannelDestination[],
): PublicationDestination[] => {
    const organizationDestinations = organizations
        .filter((organization) => organization.isSelected)
        .map(mapOrganizationPublication);
    const channelDestinations = communicationChannels
        .filter((channel) => channel.isSelected)
        .map(mapChannelPublication);
    return [...organizationDestinations, ...channelDestinations];
};

const hasAnySelectedDestination = (
    organizations: OrganizationDestination[],
    communicationChannels: ChannelDestination[],
): boolean => {
    return getSelectedPublicationDestinations(organizations, communicationChannels).length >= 1;
};

const resolveItemsQuery = (itemSelection: ItemSelection): unknown => {
    if (itemSelection.primaryKeys) {
        return itemSelection.primaryKeys;
    }
    return omitEmpty(itemSelection.itemsQuery);
};

const omitEmpty = (value: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
    if (!value) {
        return undefined;
    }
    return Object.fromEntries(
        Object.entries(value).filter(([, entry]) => !isEmptyValue(entry)),
    );
};

const resolveSelectionToSave = (itemSelection: ItemSelection): ItemSelection => {
    if (itemSelection.itemsQuery?.primaryKeys) {
        return { primaryKeys: itemSelection.itemsQuery.primaryKeys };
    }
    return itemSelection;
};

const existingSelectionId = (
    itemSelection: ItemSelection,
    publicationData?: PublicationData,
): string | undefined => {
    if (publicationData?.selectionId) {
        return publicationData.selectionId;
    }
    if (itemSelection.itemsQuery?.selectionId) {
        return String(itemSelection.itemsQuery.selectionId);
    }
    return undefined;
};

const parseScheduleDate = (value: Date | string | number | null | undefined): Date | undefined => {
    if (isNil(value)) {
        return undefined;
    }
    if (value instanceof Date) {
        return value;
    }
    return new Date(value);
};

const restorePlanOptionsFromJob = (job: PublicationJob): PlanOptions => {
    const schedule = { ...(job.schedule ?? {}) };
    if (!isNil(schedule.startDateTime)) {
        schedule.startDateTime = parseScheduleDate(schedule.startDateTime);
    }
    if (!isNil(schedule.endDateTime)) {
        schedule.endDateTime = parseScheduleDate(schedule.endDateTime);
    }
    if (!isNil(schedule.nextRunDateTime)) {
        schedule.nextRunDateTime = parseScheduleDate(schedule.nextRunDateTime);
    }
    return {
        action: 'SCHEDULE_PLAN',
        actionRepeat: !isNil(schedule.repeatType || schedule.repeatEvery || schedule.endDateTime),
        job: {
            ...job,
            schedule,
        },
    };
};

const buildJobPayload = ({
    selectionId,
    mode,
    organizations,
    communicationChannels,
    itemsByChannel,
    plans,
    planOptions,
}: {
    selectionId: string;
    mode: PublicationMode;
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
    itemsByChannel: ItemsByChannel;
    plans: CommunicationPlans;
    planOptions: PlanOptions;
}): PublicationJob => {
    const publicationDestinations = getSelectedPublicationDestinations(organizations, communicationChannels);
    const organizationDestinations = publicationDestinations.filter(
        (dest) => dest.destinationType === 'IN_PLATFORM',
    );
    const channelDestinations = publicationDestinations.filter(
        (dest) => dest.destinationType === 'COMMUNICATION_CHANNEL',
    );

    const selectedOrganizations = organizationDestinations.map((dest) => dest.destinationId);
    const subDestinations = publicationDestinations
        .filter((destination) => destination.subDestination !== undefined)
        .map((element) => `${element.destinationId}:${element.subDestination}`);
    const selectedChannels = channelDestinations
        .filter((channel) => {
            return subDestinations.indexOf(`${channel.destinationId}:${channel.subDestination}`) === -1;
        })
        .map((channel) => channel.destinationId);

    const valuesPayload = buildPublicationValuesPayload(
        channelDestinations,
        communicationChannels,
        itemsByChannel,
        plans,
        mode,
    );

    const job: PublicationJob = {
        ...planOptions.job,
        type: 'PUBLICATION',
        publicationType: mode === PUBLICATION_MODE.DEPUBLISH ? 'DELETE' : 'ADD',
        destinationChannelIds: selectedChannels,
        destinationOrganizationIds: selectedOrganizations,
        channelSubDestinations: subDestinations,
        publicationValues: valuesPayload.publicationValues,
        publicationValuesPerDestinationChannel: valuesPayload.publicationValuesPerDestinationChannel,
        selectionId,
    };

    const schedule = { ...(job.schedule ?? {}), active: true };
    if (!planOptions.actionRepeat) {
        job.schedule = { active: true, startDateTime: schedule.startDateTime };
    } else {
        job.schedule = schedule;
    }

    return job;
};

const uniqueChannelIdsWithPublicationCategory = (
    channels: ChannelDestination[],
    plans: CommunicationPlans,
    mode: PublicationMode,
): ChannelDestination[] => {
    return channels.filter((channel) => {
        return !isEmptyValue(getPublicationItemCategoryOfChannel(channel, plans, mode));
    });
};

export {
    applySavedValuesToItem,
    buildJobPayload,
    buildPublicationValuesPayload,
    existingSelectionId,
    getChannelId,
    getPublicationAttributes,
    getPublicationItemCategoryOfChannel,
    getSavedPublicationValues,
    getSavedValuesForChannel,
    getSelectedPublicationDestinations,
    hasAnySelectedDestination,
    mergeChannelDefaultItem,
    pickPublicationValuesForPlan,
    resolveItemsQuery,
    resolveSelectionToSave,
    restorePlanOptionsFromJob,
    uniqueChannelIdsWithPublicationCategory,
};
